import { Box, CircularProgress, Grid, type SxProps, Tab, Tabs, Typography } from '@mui/material'
import CryptoJS from 'crypto-js'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnonymousConversationImageList } from '../components/AnonymousConversationImageList'
import { AnonymousConversationList } from '../components/AnonymousConversationList'
import { AnonymousConversationSummary } from '../components/AnonymousConversationSummary'
import { ConversationDelButton } from '../components/ConversationDelButton'
import { ConversationNotification } from '../components/ConversationNotification'
import { DownloadButton } from '../components/DownloadButton'
import { ModalConfirmDialogMobile } from '../components/ModalConfirmDialogMobile'
import { RealtimeDiagramPreview } from '../components/RealtimeDiagramPreview'
import { loadStatus } from '../constants/loadStatus'
import { autoReloadTime } from '../constants/reload'
import { useAuth } from '../hooks/useAuth'
import { useC802ConversationGet } from '../hooks/useC802ConversationGet'
import { useC803ConversationSummaryGet } from '../hooks/useC803ConversationSummaryGet'
import { useC806AnonymousLogin } from '../hooks/useC806AnonymousLogin'
import { useC807ConversationDownload } from '../hooks/useC807ConversationDownload'
import { useC808SummaryDownload } from '../hooks/useC808SummaryDownload'
import { useC809ConversationDelete } from '../hooks/useC809ConversationDelete'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import type {
  ConversationContentInfo,
  ConversationIllustrationInfo,
  ConversationSummaryInfo,
  ConversationVisualDisplayItem,
  RealtimeDiagramData,
  VisualsItemMap,
} from '../interfaces/ConversationIf'
import { LanguageContext } from '../util/LanguageContext'

type AnonymousConversationProps = {
  value: number
  index: number
  children: React.ReactNode
  onChange: (newValue: number) => void
  sx: SxProps
}

const TabPanel: React.FC<AnonymousConversationProps> = ({
  value,
  index,
  children,
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{
        zIndex: value === index ? 1 : 0,
      }}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

const buildVisualDisplayItems = (
  visualsWithRealtimeIds: string[] | undefined,
  visualsItemMap: VisualsItemMap | undefined,
  illustrations: ConversationIllustrationInfo[],
  realtimeDiagram: RealtimeDiagramData | null
): ConversationVisualDisplayItem[] => {
  if (!visualsWithRealtimeIds || !visualsItemMap) {
    return []
  }

  const result: ConversationVisualDisplayItem[] = []

  for (const visualId of visualsWithRealtimeIds) {
    const visual = visualsItemMap[visualId]
    if (!visual) continue

    if (visual.type === 'registered_illustration') {
      const matched = illustrations.find(
        img => img.illustration_id === visual.illustration_id
      )
      if (!matched) continue

      result.push({
        visualId,
        type: 'registered_illustration',
        illustration: matched,
      })
    } else if (visual.type === 'realtime_diagram') {
      if (!realtimeDiagram) continue

      result.push({
        visualId,
        type: 'realtime_diagram',
        diagram: realtimeDiagram,
      })
    }
  }

  return result
}

export const AnonymousConversation: React.FC = () => {
  // hooks
  const auth = useAuth()
  const c802 = useC802ConversationGet()
  const c803 = useC803ConversationSummaryGet()
  const c806 = useC806AnonymousLogin()
  const c807 = useC807ConversationDownload()
  const c808 = useC808SummaryDownload()
  const c809 = useC809ConversationDelete()
  const cs = useCloudStorage()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  // 会話記録配列
  const navigate = useNavigate()
  const [value, setValue] = useState(0)
  // 会話配列
  const [convs, setConvs] = useState<ConversationContentInfo>()
  // 要約配列
  const [summarys, setSumarys] = useState<ConversationSummaryInfo>()
  // スマホ用vh
  const [vh, setVh] = useState(window.innerHeight * 0.01)
  // クエリパラメータ取得
  const search = useLocation().search
  const query = new URLSearchParams(search)
  const queryConvId = query.get('conv')
  //会話記録ID デバイスID + 日時を起動時に代入
  const [conversationId, setConversationId] = useState('')

  useEffect(() => {
    const handleResize = () => setVh(window.innerHeight * 0.01)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  // ローディングステータス監視
  useEffect(() => {
    if (
      c802.loading === loadStatus.LOADING ||
      c803.loading === loadStatus.LOADING ||
      c806.loading === loadStatus.LOADING ||
      c807.loading === loadStatus.LOADING ||
      c808.loading === loadStatus.LOADING ||
      c809.loading === loadStatus.LOADING ||
      cs.loading === loadStatus.LOADING ||
      auth.loading === loadStatus.LOADING
    ) {
      // console.log(
      //   `true c802${c802.loading} c803${c803.loading} c807${c807.loading} cs${cs.loading}`
      // );
      auth.setIsDisable(true)
    } else {
      // console.log(
      //   `false c802${c802.loading} c803${c803.loading} c807${c807.loading} cs${cs.loading}`
      // );
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c802.loading, c803.loading, c807.loading, c808.loading, c809.loading, cs.loading])

  // 起動時の処理
  useEffect(() => {
    const fetchData = async () => {
      if (auth.convId === '') {
        // リロードした場合の対策
        // ログイン時に格納したローカルストレージからデータ取得
        const savedConvId = localStorage.getItem('convId')
        const encrypted = localStorage.getItem('convPassword')
        const savedPassword =
          encrypted && CryptoJS.AES.decrypt(encrypted, 'Cotopat').toString(CryptoJS.enc.Utf8)
        if (savedConvId && savedPassword) {
          if (queryConvId !== savedConvId) {
            // クエリパラメータのIDとローカルストレージのIDが異なる場合
            //ローカルストレージ削除
            localStorage.removeItem('convId')
            localStorage.removeItem('convPassword')
            // 有効期限切れ画面に遷移
            navigate('/expired')
          }

          // C806会話記録ログイン
          const result = await c806.execAnonymousLogin(savedConvId, savedPassword)

          if (result.success) {
            const signInResult = await auth.signInAnonymous(savedConvId)
            if (!signInResult.success) {
              //ローカルストレージ削除
              localStorage.removeItem('convId')
              localStorage.removeItem('convPassword')
              // 有効期限切れ画面に遷移
              navigate('/expired')
            }
          }
        } else {
          //ローカルストレージ削除
          localStorage.removeItem('convId')
          localStorage.removeItem('convPassword')

          // 有効期限切れ画面に遷移
          navigate('/expired')
        }
      }
    }
    fetchData() // 初回起動時に一度実行

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 起動時の処理
  useEffect(() => {
    const fetchData = async () => {
      if (auth.convId) {
        // C802 会話内容取得
        const result = await c802.execConversationGet(auth.convId)
        if (result.success) {
          if (result.info) {
            setConvs(result.info)
            setConversationId(
              String(result.info.deviceId) + '_' + String(result.info.conversationIdDate)
            )
            // C803 会話要約取得
            const resultSummary = await c803.execConversationSummaryGet(auth.convId)
            if (resultSummary.success) {
              if (resultSummary.info) {
                setSumarys(resultSummary.info)
              }
            } else {
              setErrorDialog(resultSummary.message!)
              setOpenDialog(true)
            }
          }
        } else {
          setErrorDialog(result.message!)
          setOpenDialog(true)
        }
      }
      // console.log("fetchData");
    }
    fetchData() // 初回起動時に一度実行

    const intervalId = setInterval(fetchData, autoReloadTime) // 図解画像ファイル期限切れ対応：59分ごとに実行

    return () => clearInterval(intervalId) // コンポーネントのアンマウント時にインターバルをクリア
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.convId])

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const onClickConversationDownload = async () => {
    // C807 会話記録ダウンロード
    const result = await c807.execConversationDownload(auth.convId)
    if (result.success) {
      // console.log("execConversationDownload successed.");
      // console.log(result);
      const resultCs = await cs.execDownloadFile(result.info!.storage_path, result.info!.file_name)
      if (!resultCs.success) {
        setErrorDialog(resultCs.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  const onClickSummaryDownload = async () => {
    // C808 会話要約ダウンロード
    const result = await c808.execSummaryDownload(auth.convId)
    if (result.success) {
      // console.log("execSummaryDownload successed.");
      // console.log(result);
      const resultCs = await cs.execDownloadFile(result.info!.storage_path, result.info!.file_name)
      if (!resultCs.success) {
        setErrorDialog(resultCs.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  const onClickConversationDelete = async () => {
    // C809 会話記録削除
    const result = await c809.execConversationDelete(auth.convId)
    if (result.success) {
      navigate('/index.html?convId=' + auth.convId)
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let noIllustrationFontSizeJsx: string
  let IllustrationFontSizeJsx: string
  switch (baseLanguage) {
    case 'ja':
    case 'tr':
      noIllustrationFontSizeJsx = '16px'
      IllustrationFontSizeJsx = '16px'
      break
    case 'de':
      noIllustrationFontSizeJsx = '13px'
      IllustrationFontSizeJsx = '10px'
      break
    case 'sv':
      noIllustrationFontSizeJsx = '14px'
      IllustrationFontSizeJsx = '11px'
      break
    default:
      noIllustrationFontSizeJsx = '16px'
      IllustrationFontSizeJsx = '12px'
  }

  const realtimeDiagram = convs?.realtimeDiagram ?? null

  /**
   * 新版VISUALSデータかどうか。
   *
   * 旧版では BackEnd が visuals* をレスポンスに追加しないため、
   * すべて undefined になる。
   *
   * 新版では visualsVersion / visualsWithRealtimeIds / visualsItemMap の
   * いずれかが存在する。
   *
   * 注意:
   * visualsWithRealtimeIds = [] でも新版として扱う。
   */
  const isVisualsRecord =
    convs?.visualsVersion !== undefined ||
    convs?.visualsWithRealtimeIds !== undefined ||
    convs?.visualsItemMap !== undefined ||
    convs?.editVisualsWithRealtimeIds !== undefined ||
    convs?.editVisualsItemMap !== undefined

  /**
   * 編集後VISUALSが存在するか。
   *
   * length > 0 では判定しない。
   * editVisualsWithRealtimeIds = [] は
   * 「編集後にVISUALSを全削除した状態」として有効。
   */
  const hasEditVisuals =
    convs?.editVisualsWithRealtimeIds !== undefined ||
    convs?.editVisualsItemMap !== undefined

  /**
   * VISUALS表示モードを使うか。
   *
   * 旧版: false
   * 新版: true
   */
  const useVisualTimelineDisplay = isVisualsRecord

  /**
   * 表示対象VISUALS。
   *
   * 編集後VISUALSがある場合は、空配列でも編集後を優先する。
   */
  const displayVisualsWithRealtimeIds = hasEditVisuals
    ? convs?.editVisualsWithRealtimeIds ?? []
    : convs?.visualsWithRealtimeIds ?? []

  const displayVisualsItemMap = hasEditVisuals
    ? convs?.editVisualsItemMap ?? {}
    : convs?.visualsItemMap ?? {}

  /**
   * 旧版向け：会話本文が編集済みかどうか。
   *
   * 旧版では visuals* がないため、登録図解は従来フィールドで判断する。
   */
  const hasEditedConversation =
    convs?.conversationList?.some(
      item => item.edit_text !== undefined && item.edit_text !== null
    ) ?? false

  /**
   * 表示対象の登録図解。
   *
   * 新版:
   *   VISUALS内の registered_illustration 解決用に使う。
   *   hasEditVisuals がある場合は edit_illustrationList を見る。
   *
   * 旧版:
   *   未編集なら org_illustrationList
   *   編集済みなら edit_illustrationList
   */
  const displayIllustrations = isVisualsRecord
    ? hasEditVisuals
      ? convs?.edit_illustrationList ?? []
      : convs?.org_illustrationList ?? []
    : hasEditedConversation
      ? convs?.edit_illustrationList ?? []
      : convs?.org_illustrationList ?? []

  const visualDisplayItems = useVisualTimelineDisplay
    ? buildVisualDisplayItems(
        displayVisualsWithRealtimeIds,
        displayVisualsItemMap,
        displayIllustrations,
        realtimeDiagram
      )
    : []

  /**
   * 図解タブを表示するか。
   *
   * 旧版:
   *   登録図解があれば表示。
   *
   * 新版:
   *   VISUALSの表示項目が1件以上あれば表示。
   *   visuals* が空なら表示しない。
   *   旧版表示へフォールバックしない。
   */
  const hasDiagramTab = useVisualTimelineDisplay
    ? visualDisplayItems.length > 0
    : displayIllustrations.length > 0

  return (
    <>
      {openDialog && (
        <ModalConfirmDialogMobile
          open={openDialog}
          text={errorDialog}
          onClick={handleDialogClose}
        />
      )}
      <Grid container direction="column" justifyContent="center" alignItems="center">
        <Grid item mt="16px" justifyContent="center">
          <CotopatImage width="100px" height="26px" />
        </Grid>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          {(c802.loading === loadStatus.LOADING ||
            c803.loading === loadStatus.LOADING ||
            c806.loading === loadStatus.LOADING ||
            c807.loading === loadStatus.LOADING ||
            c808.loading === loadStatus.LOADING ||
            c809.loading === loadStatus.LOADING ||
            cs.loading === loadStatus.LOADING ||
            auth.loading === loadStatus.LOADING) && <CircularProgress />}
        </Box>
        {hasDiagramTab ? (
          <>
            {/**** 図解ありの場合 ****/}
            <Grid item mt="14px">
              <Tabs value={value} onChange={handleChange}>
                <Tab
                  label={translation['label_full_conversation']}
                  sx={{
                    width: '108px',
                    height: '40px',
                    font: 'normal normal bold 16px/23px Noto Sans JP',
                    fontSize: IllustrationFontSizeJsx,
                    color: '#282828',
                    background: '#FFFFFF 0% 0% no-repeat padding-box',
                    boxShadow: '0px 2px 2px #00000029',
                    borderRadius: '10px 10px 0px 0px',
                    opacity: 1,
                    '&.Mui-selected': {
                      // 選択されたタブのスタイル
                      color: '#282828',
                    },
                    zIndex: value === 0 ? 2 : value === 1 ? 0 : 1,
                  }}
                />

                <Tab
                  label={translation['label_diagram']}
                  sx={{
                    width: '108px',
                    height: '40px',
                    font: 'normal normal bold 16px/23px Noto Sans JP',
                    fontSize: IllustrationFontSizeJsx,
                    color: '#282828',
                    background: '#FFF16F 0% 0% no-repeat padding-box',
                    borderRadius: '10px 10px 0 0',
                    opacity: 1,
                    '&.Mui-selected': {
                      // 選択されたタブのスタイル
                      color: '#282828',
                    },
                    zIndex: value === 0 ? 1 : value === 1 ? 2 : 1,
                  }}
                />

                <Tab
                  label={translation['label_summary']}
                  sx={{
                    width: '108px',
                    height: '40px',
                    font: 'normal normal bold 16px/23px Noto Sans JP',
                    fontSize: IllustrationFontSizeJsx,
                    color: '#FFFFFF',
                    background: '#282828 0% 0% no-repeat padding-box',
                    borderRadius: '10px 10px 0 0',
                    opacity: 1,
                    '&.Mui-selected': {
                      // 選択されたタブのスタイル
                      color: '#FFFFFF',
                    },
                    zIndex: value === 0 ? 0 : value === 1 ? 0 : 2,
                  }}
                />
              </Tabs>
            </Grid>
            <Grid item sx={{ bgcolor: 'white', width: '100%' }} xs={12}>
              {/* 図解あり：会話全文 */}
              <TabPanel
                value={value}
                index={0}
                onChange={setValue}
                sx={{
                  height: `calc(${vh * 100}px  - 112px - 77px)`,
                  width: '100%',
                }}
              >
                <div style={{ height: `calc(${vh * 100}px - 112px - 77px)` }}>
                  <AnonymousConversationList
                    date={convs?.date}
                    items={convs?.conversationList}
                    id={conversationId}
                  />
                </div>
                <div
                  style={{
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}
                >
                  <DownloadButton
                    title={translation['button_download']}
                    onClick={onClickConversationDownload}
                  />
                  <ConversationDelButton
                    text={conversationId}
                    onClick={onClickConversationDelete}
                    onDel={onClickConversationDelete}
                  />
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                  <Typography
                    mb="2px"
                    sx={{
                      font: 'normal normal normal 16px/24px Noto Sans JP',
                      textAlign: 'left',
                      letterSpacing: '0px',
                      color: '#282828',
                    }}
                  >
                    {translation['copyright_info']}
                  </Typography>
                </Box>
              </TabPanel>
              {/* 図解あり：図解 */}
              <TabPanel
                value={value}
                index={1}
                onChange={setValue}
                sx={{
                  height: `calc(${vh * 100}px  - 112px )`,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    height: `calc(${vh * 100}px - 112px)`,
                    width: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    bgcolor: 'white',
                    pb: '16px',
                  }}
                >
                  {useVisualTimelineDisplay ? (
                    visualDisplayItems.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '16px',
                          pt: '16px',
                          pb: '16px',
                        }}
                      >
                        {visualDisplayItems.map(item => {
                          if (item.type === 'realtime_diagram') {
                            return (
                              <Box
                                key={item.visualId}
                                sx={{
                                  width: '100%',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    textAlign: 'center',
                                    color: '#282828',
                                    borderBottom: '2px solid #282828',
                                    pb: '24px',
                                    mb: '22px',
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  {convs?.date && (
                                    <Typography
                                      sx={{
                                        font: 'normal normal normal 14px/20px Noto Sans JP',
                                        color: '#282828',
                                      }}
                                    >
                                      {convs.date}
                                    </Typography>
                                  )}

                                  {conversationId && (
                                    <>
                                      <Typography
                                        sx={{
                                          mt: '4px',
                                          font: 'normal normal 18px/24px Noto Sans JP',
                                          color: '#282828',
                                        }}
                                      >
                                        会話記録ID:
                                      </Typography>

                                      <Typography
                                        sx={{
                                          font: 'normal normal normal 18px/24px Noto Sans JP',
                                          color: '#282828',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'anywhere',
                                        }}
                                      >
                                        {conversationId}
                                      </Typography>
                                    </>
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <RealtimeDiagramPreview diagram={item.diagram} />
                                </Box>
                              </Box>
                            )
                          }

                          return (
                            <Box
                              key={item.visualId}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              <AnonymousConversationImageList
                                date={convs?.date}
                                items={[item.illustration]}
                                id={conversationId}
                              />
                            </Box>
                          )
                        })}
                      </Box>
                    ) : null
                  ) : (
                    <>
                      {displayIllustrations.length > 0 && (
                        <AnonymousConversationImageList
                          date={convs?.date}
                          items={displayIllustrations}
                          id={conversationId}
                        />
                      )}
                    </>
                  )}
                </Box>
              </TabPanel>
              {/* 図解あり：要約 */}
              <TabPanel
                value={value}
                index={2}
                onChange={setValue}
                sx={{
                  height: `calc(${vh * 100}px  - 112px - 77px)`,
                  width: '100%',
                }}
              >
                <div style={{ height: `calc(${vh * 100}px - 112px - 77px)` }}>
                  <AnonymousConversationSummary
                    date={summarys?.date}
                    text={summarys?.text}
                    text2={summarys?.text2}
                    text_lang={summarys?.text_lang}
                    text2_lang={summarys?.text2_lang}
                    id={conversationId}
                  />
                </div>
                <ConversationNotification text={translation['label_AI_Poricy']} />
                <div
                  style={{
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}
                >
                  <DownloadButton
                    title={translation['button_download']}
                    onClick={onClickSummaryDownload}
                  />
                  <ConversationDelButton
                    text={conversationId}
                    onClick={onClickConversationDelete}
                    onDel={onClickConversationDelete}
                  />
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                  <Typography
                    mb="2px"
                    sx={{
                      font: 'normal normal normal 16px/24px Noto Sans JP',
                      textAlign: 'left',
                      letterSpacing: '0px',
                      color: '#282828',
                    }}
                  >
                    {translation['copyright_info']}
                  </Typography>
                </Box>
              </TabPanel>
            </Grid>
            {/* </Paper> */}
          </>
        ) : (
          <>
            {/**** 図解なしの場合 ****/}
            <Grid item mt="14px">
              <Tabs value={value} onChange={handleChange}>
                <Tab
                  label={translation['label_full_conversation']}
                  sx={{
                    width: '150px',
                    height: '40px',
                    font: 'normal normal bold 16px/23px Noto Sans JP',
                    fontSize: noIllustrationFontSizeJsx,
                    color: '#282828',
                    background: '#FFFFFF 0% 0% no-repeat padding-box',
                    boxShadow: '0px 2px 2px #00000029',
                    borderRadius: '10px 10px 0px 0px',
                    opacity: 1,
                    '&.Mui-selected': {
                      // 選択されたタブのスタイル
                      color: '#282828',
                    },
                    zIndex: value === 0 ? 1 : 0,
                  }}
                />
                <Tab
                  label={translation['label_summary']}
                  sx={{
                    width: '150px',
                    height: '40px',
                    font: 'normal normal bold 16px/23px Noto Sans JP',
                    fontSize: noIllustrationFontSizeJsx,
                    color: '#FFFFFF',
                    background: '#282828 0% 0% no-repeat padding-box',
                    borderRadius: '10px 10px 0 0',
                    opacity: 1,
                    '&.Mui-selected': {
                      // 選択されたタブのスタイル
                      color: '#FFFFFF',
                    },
                    zIndex: value === 0 ? 0 : 1,
                  }}
                />
              </Tabs>
            </Grid>

            <Grid item sx={{ bgcolor: 'white', width: '100%' }} xs={12}>
              {/* 図解なし：会話全文 */}
              <TabPanel
                value={value}
                index={0}
                onChange={setValue}
                sx={{
                  height: `calc(${vh * 100}px  - 112px - 77px)`,
                  width: '100%',
                }}
              >
                <div style={{ height: `calc(${vh * 100}px - 112px - 77px)` }}>
                  <AnonymousConversationList
                    date={convs?.date}
                    items={convs?.conversationList}
                    id={conversationId}
                  />
                </div>
                <div
                  style={{
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}
                >
                  <DownloadButton
                    title={translation['button_download']}
                    onClick={onClickConversationDownload}
                  />
                  <ConversationDelButton
                    text={conversationId}
                    onClick={onClickConversationDelete}
                    onDel={onClickConversationDelete}
                  />
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                  <Typography
                    mb="2px"
                    sx={{
                      font: 'normal normal normal 16px/24px Noto Sans JP',
                      textAlign: 'left',
                      letterSpacing: '0px',
                      color: '#282828',
                    }}
                  >
                    {translation['copyright_info']}
                  </Typography>
                </Box>
              </TabPanel>
              {/* 図解なし：要約 */}
              <TabPanel
                value={value}
                index={1}
                onChange={setValue}
                sx={{
                  height: `calc(${vh * 100}px  - 112px - 77px)`,
                  width: '100%',
                }}
              >
                <div style={{ height: `calc(${vh * 100}px - 112px - 77px)` }}>
                  <AnonymousConversationSummary
                    date={summarys?.date}
                    text={summarys?.text}
                    text2={summarys?.text2}
                    text_lang={summarys?.text_lang}
                    text2_lang={summarys?.text2_lang}
                    id={conversationId}
                  />
                </div>
                <ConversationNotification text={translation['label_AI_Poricy']} />
                <div
                  style={{
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}
                >
                  <DownloadButton
                    title={translation['button_download']}
                    onClick={onClickSummaryDownload}
                  />
                  <ConversationDelButton
                    text={conversationId}
                    onClick={onClickConversationDelete}
                    onDel={onClickConversationDelete}
                  />
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                  <Typography
                    mb="2px"
                    sx={{
                      font: 'normal normal normal 16px/24px Noto Sans JP',
                      textAlign: 'left',
                      letterSpacing: '0px',
                      color: '#282828',
                    }}
                  >
                    {translation['copyright_info']}
                  </Typography>
                </Box>
              </TabPanel>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}
